import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft, Mail, Phone, MessageCircle, Tag, Pencil, Trash2,
  Calendar, MessageSquare, Activity as ActivityIcon, FileText, AlertTriangle,
  HelpCircle, ArrowUpRight, StickyNote, ChevronRight, Send,
  Briefcase, ShieldAlert, Timer, HelpCircle as EnquiryIcon,
  Clock, UserPlus, Landmark, BadgeCheck,
} from "lucide-react";
import { Sidebar } from "../components/Sidebar";
import { useChainStore, openEditContact } from "../data/chainStore";
import { useCanManageContacts } from "../hooks/useDealAccess";
import { initialsOf } from "../components/contacts/AddContactModal";
import { ContactQuickActions } from "../components/contacts/ContactQuickActions";
import { ContactTransactionsModal } from "../components/contacts/ContactTransactionsModal";
import { whatsappHref, telHref, mailtoHref } from "../lib/contactLinks";
import { dealStatusFor } from "../data/dealHealth";
import { nodeIdForStage, nodeById } from "../data/transactionJourney";
import { getDealExchangeTarget } from "../data/chainStore";
import { buildRequestDocUrl, isDocRequestRelevant } from "../lib/documentRequestLaunch";
import { toast } from "sonner";
import { useDealEmails } from "../lib/useDealEmails";
import { DemoBadge, isSimulatedDemo } from "../components/ui/DemoBadge";

const STATUS_TONES = {
  Active:   "bg-emerald-50 text-emerald-700",
  Pending:  "bg-amber-50 text-amber-700",
  Archived: "bg-slate-100 text-slate-600",
};

const ROLE_TONES = {
  Buyer:           "bg-indigo-50 text-indigo-700",
  Seller:          "bg-blue-50 text-blue-700",
  Solicitor:       "bg-amber-50 text-amber-700",
  "Mortgage Broker": "bg-emerald-50 text-emerald-700",
  "Estate Agent":  "bg-indigo-50 text-indigo-700",
  "Managing Agent": "bg-cyan-50 text-cyan-700",
  Surveyor:        "bg-slate-100 text-slate-700",
  Other:           "bg-slate-100 text-slate-700",
};

/* iter-alignment · P37 — CRM Transaction Linking (unchanged SSOT). */
const _roleForDeal = (deal, contactId) => {
  if (!deal || !contactId) return "Contact";
  if (deal.buyerId === contactId)           return "Buyer";
  if (deal.sellerId === contactId)          return "Seller";
  if (deal.buyerSolicitorId === contactId)  return "Buyer Solicitor";
  if (deal.sellerSolicitorId === contactId) return "Seller Solicitor";
  if (deal.solicitorId === contactId)       return "Solicitor";
  if (deal.brokerId === contactId)          return "Mortgage Broker";
  if (deal.agentId === contactId)           return "Estate Agent";
  return "Contact";
};

const _HEALTH_TONES = {
  healthy: "bg-emerald-50 text-emerald-700 border-emerald-200",
  watch:   "bg-amber-50 text-amber-700 border-amber-200",
  at_risk: "bg-rose-50 text-rose-700 border-rose-200",
};

/* iter-alignment · P47 — helpers.
 *
 * All aggregation reads directly from the existing store SSOT:
 *   • `deal.activity[]`     — Activity SSOT (already loaded)
 *   • `deal.messages[]`     — Communication SSOT (already loaded)
 *   • `deal.blockers[]`     — Outstanding Actions SSOT (already loaded)
 *   • `deal.enquiries[]`    — Enquiry SSOT when present client-side
 *   • `getPilotDocumentRequests(dealId)` — Document Request SSOT
 *
 * Zero new state, zero new endpoints, zero duplicate logic. */

const _fmtTargetDate = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
};

const _fmtRelative = (ts) => {
  if (!ts) return "";
  const t = typeof ts === "number" ? ts : Date.parse(ts);
  if (Number.isNaN(t)) return "";
  const diff = Date.now() - t;
  const days = Math.floor(diff / 86400000);
  if (days < 1) return "today";
  if (days === 1) return "1d ago";
  if (days < 7) return `${days}d ago`;
  const weeks = Math.floor(days / 7);
  if (weeks < 4) return `${weeks}w ago`;
  const months = Math.max(1, Math.floor(days / 30));
  return `${months}mo ago`;
};

/* Waiting-side derivation.
 *
 * Owner role is the canonical SSOT the Deal Room + Right-rail
 * already read for blockers.  `agent` = our side (ChainFlow
 * operator), everything else = counterparty.  Same mapping used
 * across `PeoplePage`, `DealRoomCanonicalPage`, `activity feed`. */
const _waitingSide = (blocker) => {
  const owner = String(blocker?.owner_role || "").toLowerCase();
  if (owner === "agent" || owner === "operator" || owner === "us") return "us";
  return "them";
};

const _blockerTitle = (b) =>
  b?.title || b?.label || b?.description || b?.summary || "Response pending";
const _blockerDetail = (b) =>
  b?.owner_label || b?.detail || b?.sub || b?.party || null;

/* iter-alignment · Broker Contact Profile Refinement (2026-02-28)
 * ---------------------------------------------------------------
 * Mortgage stage projection for the Broker profile's Live
 * Transactions row.  Reads ONLY canonical fields already surfaced
 * on the deal envelope (`stage`, `mortgageStatus`, `isCashBuyer`,
 * `completedStepsApi`) — no synthetic tokens, no fixture fallbacks.
 * Returns `null` when the SSOT cannot supply a reliable stage so
 * the row hides the pill cleanly (spec: "hide unavailable
 * secondary information cleanly · do not fabricate a stage"). */
const _MORTGAGE_STAGE_TONES = {
  AIP:         "bg-slate-50 text-slate-700 border-slate-200",
  Application: "bg-indigo-50 text-indigo-700 border-indigo-200",
  Valuation:   "bg-amber-50 text-amber-700 border-amber-200",
  Offer:       "bg-emerald-50 text-emerald-700 border-emerald-200",
  Ready:       "bg-emerald-50 text-emerald-700 border-emerald-200",
};
const _resolveMortgageStage = (deal) => {
  if (!deal) return null;
  // iter-alignment · Funding Basis SSOT · 2026-03-09 — Read
  // through the canonical funding_basis field first; fall back
  // to the compat is_cash_buyer boolean only when funding_basis
  // is null / unset.  Matches the transactionJourney.js
  // resolveMortgage() resolver so the Contact Profile's
  // Mortgage-track visibility can never split-brain from the
  // Deal Room journey rail.
  const _fb = String(deal.funding_basis || deal.fundingBasis || "").toLowerCase();
  if (_fb === "cash") return null;
  if (!_fb && (deal.isCashBuyer === true || deal.is_cash_buyer === true)) return null;
  const completed = new Set(Array.isArray(deal.completedStepsApi) ? deal.completedStepsApi : []);
  const stage = String(deal.stage || "").toLowerCase();
  if (stage === "exchanged" || stage === "completed") return "Ready";
  if (stage === "exchange_ready" || completed.has("mortgage_offer")) return "Offer";
  if (stage === "mortgage_offer") return "Offer";
  if (stage === "mortgage_app" || stage === "mortgage")   return "Application";
  const ms = String(deal.mortgageStatus || "").toLowerCase();
  if (ms === "offered")             return "Offer";
  if (ms === "submitted")           return "Application";
  if (ms === "in_review")           return "Application";
  if (ms === "aip" || ms === "agreed_in_principle") return "AIP";
  return null;
};

/* iter-alignment · Broker Contact Profile Refinement (2026-02-28)
 * ---------------------------------------------------------------
 * "Mortgage Offers" count.  Counts every linked deal whose
 * canonical journey shows the mortgage offer step complete OR
 * whose stage has advanced beyond mortgage offer.  Reads the
 * same `completed_steps` / `stage` SSOT the Deal Rooms list rail
 * uses so the count can never drift out of sync. */
const _hasMortgageOffer = (deal) => {
  if (!deal) return false;
  // iter-alignment · Funding Basis SSOT · 2026-03-09 — same
  // fb-first resolver used by `_resolveMortgageStage` above.
  const _fb = String(deal.funding_basis || deal.fundingBasis || "").toLowerCase();
  if (_fb === "cash") return false;
  if (!_fb && (deal.isCashBuyer === true || deal.is_cash_buyer === true)) return false;
  const completed = new Set(Array.isArray(deal.completedStepsApi) ? deal.completedStepsApi : []);
  if (completed.has("mortgage_offer")) return true;
  const stage = String(deal.stage || "").toLowerCase();
  return stage === "mortgage_offer" || stage === "exchange_ready" || stage === "exchanged" || stage === "completed";
};

/* iter-361 · F-10 · Solicitor Contact Profile Refinement
 * (2026-08-29) — compact KPI tile shared across the Solicitor
 * profile's stats strip.  Renders a calm "unavailable" state
 * when the underlying SSOT cannot supply the metric so the
 * profile never invents figures. */
const KpiTile = ({
  icon, label, value, tone = "calm",
  unavailable = false, tooltip, testid,
}) => {
  const toneClass =
    tone === "danger" ? "text-rose-700"    :
    tone === "warn"   ? "text-amber-700"   :
                        "text-slate-900";
  return (
    <div
      className="rounded-xl border border-slate-100 bg-white px-3 py-2 flex flex-col justify-center min-w-0"
      data-testid={testid}
      title={tooltip || undefined}
    >
      <div className="text-[10.5px] uppercase tracking-[0.10em] font-semibold text-slate-500 inline-flex items-center gap-1.5 min-w-0 leading-[1.2]">
        <span className="text-slate-400 shrink-0">{icon}</span>
        <span className="min-w-0">{label}</span>
      </div>
      <div
        className={`mt-0.5 text-[19px] font-display font-bold tabular-nums tracking-[-0.02em] ${unavailable ? "text-slate-400" : toneClass}`}
        data-testid={testid ? `${testid}-value` : undefined}
      >
        {value}
      </div>
    </div>
  );
};

/* Section shell — hides when `hidden` is true, standard rounded
 * card with eyebrow + heading + optional right-side chip. */
const Section = ({
  eyebrow, heading, testid, hidden, children, chip,
}) => {
  if (hidden) return null;
  return (
    <section
      className="group rounded-3xl bg-white border border-slate-100 p-4 md:p-5"
      data-testid={testid}
    >
      <div className="flex items-center justify-between mb-2">
        <div>
          <p className="cf-eyebrow cf-eyebrow-emerald">{eyebrow}</p>
          <h2 className="cf-h-section text-[16px]">{heading}</h2>
        </div>
        {chip}
      </div>
      {children}
    </section>
  );
};

export default function ContactProfilePage() {
  const { contactId } = useParams();
  const nav = useNavigate();
  const fromDealPath = useLocation().state?.fromDeal?.path || null;
  useEffect(() => { window.scrollTo(0, 0); }, [contactId]);
  const {
    findContact, dealsForContact, findProperty, ensurePropertyLoaded, deleteContact, stages,
    getPilotDocumentRequests,
  } = useChainStore();
  const contact = findContact(contactId);
  const canManage = useCanManageContacts();
  const stageLabel = (sid) => stages.find((s) => s.id === sid)?.label || sid;

  const linkedDeals = useMemo(
    () => (contact ? dealsForContact(contact.id) : []),
    [contact, dealsForContact],
  );

  /* iter-alignment · P3 · Contact Profile · Live Transaction
   * Property Data (2026-02-28)
   * ----------------------------------------------------------------
   * The initial `/api/properties` fetch caps at 200 rows for
   * bandwidth.  When a Contact is linked to a Deal Room whose
   * Property lives beyond that page, the local lookup returns
   * `null` and the Live Transactions row would fall through to
   * the generic "Property" label.  This effect closes the gap:
   * for every `propertyId` referenced by a linked deal, we ensure
   * the row is loaded into the store on demand.  `ensurePropertyLoaded`
   * dedupes in-flight fetches so N deals sharing the same Property
   * fire ONE network round-trip.  Purely additive — no new API,
   * no new state, no duplicate SSOT. */
  useEffect(() => {
    if (!Array.isArray(linkedDeals) || linkedDeals.length === 0) return;
    const seen = new Set();
    for (const d of linkedDeals) {
      const pid = d?.propertyId;
      if (!pid || seen.has(pid)) continue;
      seen.add(pid);
      if (!findProperty(pid)) {
        ensurePropertyLoaded?.(pid);
      }
    }
  }, [linkedDeals, findProperty, ensurePropertyLoaded]);

  /* P3 · Contact Profile · Transactions Management (2026-02-28).
   *
   * Local open flag for the lightweight transactions manager
   * modal.  Opens from the Quick Actions row's "Transactions"
   * button (replacing the previous scroll-to-anchor "View All
   * Transactions" affordance) and closes on backdrop click,
   * Escape or the "×" glyph. */
  const [transactionsOpen, setTransactionsOpen] = useState(false);

  /* Address lookup memoised per deal so downstream aggregators
   * (matters, actions, activity, comms, doc requests) never do a
   * redundant property scan. */
  const dealAddresses = useMemo(() => {
    const m = new Map();
    for (const d of linkedDeals) {
      const prop = findProperty(d.propertyId);
      m.set(d.id, prop?.address?.split(",")[0] || d.name || "Property");
    }
    return m;
  }, [linkedDeals, findProperty]);

  /* Outstanding Actions — flatten `deal.blockers` across all
   * linked deals, exclude resolved rows, split by owner_role.
   * iter-P62 — also roll up the number of DISTINCT matters
   * currently held up by any open blocker for the compact
   * inline summary chip in the section header. */
  const outstandingActions = useMemo(() => {
    const us = [];
    const them = [];
    const blockedDealIds = new Set();
    for (const d of linkedDeals) {
      const list = Array.isArray(d.blockers) ? d.blockers : [];
      for (const b of list) {
        if (!b || b.status === "resolved") continue;
        const row = {
          dealId: d.id,
          address: dealAddresses.get(d.id),
          title: _blockerTitle(b),
          detail: _blockerDetail(b),
          days: b.days ?? b.age_days ?? null,
          id: b.id || `${d.id}-${_blockerTitle(b)}`,
        };
        (_waitingSide(b) === "us" ? us : them).push(row);
        blockedDealIds.add(d.id);
      }
    }
    return {
      us,
      them,
      total: us.length + them.length,
      blockedMattersCount: blockedDealIds.size,
    };
  }, [linkedDeals, dealAddresses]);

  /* Open Enquiries — reuse `deal.enquiries` when the store has
   * hydrated it client-side.  Excludes resolved / done rows. */
  const openEnquiries = useMemo(() => {
    const rows = [];
    for (const d of linkedDeals) {
      const list = Array.isArray(d.enquiries) ? d.enquiries : [];
      for (const e of list) {
        const status = String(e?.status || "").toLowerCase();
        if (status === "resolved" || status === "done" || status === "closed") continue;
        rows.push({
          dealId: d.id,
          address: dealAddresses.get(d.id),
          body: e?.body || e?.title || "Enquiry",
          id: e?.id || `${d.id}-enq-${rows.length}`,
        });
      }
    }
    return rows;
  }, [linkedDeals, dealAddresses]);

  /* Recent Activity — flatten `deal.activity[]` across all linked
   * deals, sort desc by ts, top 8.  Same shape the Deal Room feed
   * already renders (`at`, `text`, `who`). */
  const recentActivity = useMemo(() => {
    const rows = [];
    for (const d of linkedDeals) {
      const list = Array.isArray(d.activity) ? d.activity : [];
      for (const a of list) {
        rows.push({
          dealId: d.id,
          address: dealAddresses.get(d.id),
          text: a?.text || a?.description || a?.title || "Activity",
          at: a?.at ?? a?.created_at ?? a?.timestamp ?? null,
          id: a?.id || `${d.id}-act-${rows.length}`,
        });
      }
    }
    return rows
      .filter((r) => r.at !== null)
      .sort((a, b) => (Date.parse(b.at) || b.at) - (Date.parse(a.at) || a.at))
      .slice(0, 8);
  }, [linkedDeals, dealAddresses]);

  /* Recent Communication — flatten `deal.messages[]` across all
   * linked deals, sort desc by `at`, top 5.  Excludes internal
   * team-only rows (`internal === true`). */
  const contactEmail = String(contact?.email || "").trim().toLowerCase();
  const dealEmails = useDealEmails(linkedDeals.map((d) => d.id), !!contactEmail);

  const recentComms = useMemo(() => {
    const rows = [];
    /* Canonical email records (Communication Centre) where this contact is a sender or recipient. */
    for (const e of dealEmails) {
      const addrs = [e.from_email, ...(e.to_emails || []), ...(e.cc_emails || [])]
        .map((a) => String((a && a.email) || a || "").toLowerCase());
      if (!addrs.includes(contactEmail)) continue;
      rows.push({
        dealId: e.deal_id,
        address: dealAddresses.get(e.deal_id),
        body: e.subject || e.snippet || "",
        at: e.received_at || null,
        id: `mail-${e.id}`,
        demo: isSimulatedDemo(e),
      });
    }
    for (const d of linkedDeals) {
      const list = Array.isArray(d.messages) ? d.messages : [];
      for (const m of list) {
        if (m?.internal === true) continue;
        rows.push({
          dealId: d.id,
          address: dealAddresses.get(d.id),
          body: m?.body || m?.text || "",
          at: m?.at ?? m?.created_at ?? null,
          id: m?.id || `${d.id}-msg-${rows.length}`,
        });
      }
    }
    return rows
      .filter((r) => r.at !== null && r.body)
      .sort((a, b) => (Date.parse(b.at) || b.at) - (Date.parse(a.at) || a.at))
      .slice(0, 5);
  }, [linkedDeals, dealAddresses, dealEmails, contactEmail]);

  /* Open Document Requests — reuse the SSOT selector already used
   * by the Documents Hub.  Filters to pending rows only.
   *
   * iter-alignment · P49.1 — Row carries a due-status token
   * derived from the existing `due_date` field on the request:
   *   • future  → neutral grey (calm, no urgency)
   *   • today   → amber
   *   • overdue → red
   * No new SSOT — reads the same `due_date` the Documents Hub
   * reads.  When a request has no `due_date`, the row falls back
   * to the neutral tone. */
  const openDocRequests = useMemo(() => {
    if (typeof getPilotDocumentRequests !== "function") return [];
    const rows = [];
    const now = Date.now();
    const startOfToday = new Date(now); startOfToday.setHours(0, 0, 0, 0);
    const startOfTomorrow = new Date(startOfToday.getTime() + 86400000);
    for (const d of linkedDeals) {
      const list = getPilotDocumentRequests(d.id) || [];
      for (const r of list) {
        const status = String(r?.status || r?.state || "pending").toLowerCase();
        if (status === "fulfilled" || status === "approved" || status === "closed") continue;
        // Derive urgency band from the same due-date SSOT the
        // Documents Hub uses — no duplicate scheduling logic.
        const dueRaw = r?.due_date || r?.due_at || r?.due || null;
        const dueTs = dueRaw ? Date.parse(dueRaw) : null;
        let dueBand = "future";     // grey
        let dueLabel = null;
        if (dueTs) {
          if (dueTs < startOfToday.getTime()) {
            dueBand = "overdue";
            dueLabel = "Overdue";
          } else if (dueTs < startOfTomorrow.getTime()) {
            dueBand = "today";
            dueLabel = "Due today";
          } else {
            dueBand = "future";
            const days = Math.round((dueTs - now) / 86400000);
            dueLabel = days <= 1 ? "Due in 1 day" : `Due in ${days} days`;
          }
        }
        rows.push({
          dealId: d.id,
          address: dealAddresses.get(d.id),
          title: r?.title || r?.label || r?.name || "Document requested",
          from: r?.requested_from_name || r?.owner_label || null,
          dueBand,
          dueLabel,
          id: r?.id || `${d.id}-doc-${rows.length}`,
        });
      }
    }
    return rows;
  }, [linkedDeals, dealAddresses, getPilotDocumentRequests]);

  /* iter-361 · F-10 · Solicitor Contact Profile Refinement
   * (2026-08-29)
   * ----------------------------------------------------------------
   * When the contact's role is Solicitor we surface a compact KPI
   * strip (Active Matters · At Risk · Avg Response · Outstanding
   * Enquiries · Last Contact) plus a pair of primary CTAs
   * (Instruct/Refer · Add to Transaction).  Every value is sourced
   * from canonical SSOT already resolved above — no new endpoints,
   * no duplicate state, no fixture fallbacks.  A metric we cannot
   * calculate reliably today (Avg Response Time — the deal.messages
   * feed lacks direction/timing metadata) is surfaced as a calm
   * "—" placeholder rather than an invented figure. */
  const isSolicitor = /solicitor/i.test(contact?.role || "");
  const isBroker    = /broker/i.test(contact?.role || "");
  const solicitorKpis = useMemo(() => {
    if (!isSolicitor) return null;
    // Active matters = every linked deal that is not archived or
    // completed — the same rule the Deal Rooms rail uses.
    const activeMatters = linkedDeals.filter((d) => {
      const s = String(d.stage || "").toLowerCase();
      return !(d.archived === true || s === "completed" || s === "complete");
    });
    const atRisk = activeMatters.filter((d) => {
      const band = dealStatusFor(d)?.band;
      return band === "critical" || band === "warning";
    }).length;
    // "Last Contact" — most recent activity row across all linked
    // matters.  Reuses the same aggregate the Recent Activity card
    // renders, so a refresh keeps them in lock-step.
    const lastContactIso = recentActivity[0]?.at || null;
    return {
      activeCount:    activeMatters.length,
      atRiskCount:    atRisk,
      // Metric currently un-derivable from canonical SSOT:
      //   • deal.messages lacks direction + response-time metadata
      //   • no aggregated `avg_response_ms` on the contact/firm
      // Surface a calm "—" until the SSOT is extended — never
      // invent a figure just to fill the tile.
      avgResponse:    null,
      enquiriesCount: openEnquiries.length,
      lastContactAt:  lastContactIso,
    };
  }, [isSolicitor, linkedDeals, openEnquiries, recentActivity]);

  /* iter-alignment · Broker Contact Profile Refinement (2026-02-28)
   * ---------------------------------------------------------------
   * KPI strip for the Mortgage Broker profile.  Every metric is
   * derived from canonical SSOT already resolved on the page — no
   * new state, no new endpoint, no fixture fallback.  Metrics we
   * cannot compute reliably (e.g. `lastContactAt` when the
   * activity aggregate is empty) surface as a calm "—" placeholder
   * — never invented values (spec).
   *
   *   • Active Cases        — linked deals not archived / completed
   *   • At Risk             — subset of Active with dealHealth band
   *                           ∈ {critical, warning}
   *   • Mortgage Offers     — count of linked deals whose mortgage
   *                           offer step is complete OR whose stage
   *                           has advanced past it (same rule the
   *                           Deal Rooms list rail uses)
   *   • Outstanding Actions — total open blockers across matters
   *                           (reuses `outstandingActions.total`)
   *   • Last Contact        — most recent activity across matters
   *                           (reuses `recentActivity[0].at`)
   */
  const brokerKpis = useMemo(() => {
    if (!isBroker) return null;
    const activeMatters = linkedDeals.filter((d) => {
      const s = String(d.stage || "").toLowerCase();
      return !(d.archived === true || s === "completed" || s === "complete");
    });
    const atRisk = activeMatters.filter((d) => {
      const band = dealStatusFor(d)?.band;
      return band === "critical" || band === "warning";
    }).length;
    const mortgageOffers = linkedDeals.filter(_hasMortgageOffer).length;
    return {
      activeCount:         activeMatters.length,
      atRiskCount:         atRisk,
      mortgageOffersCount: mortgageOffers,
      outstandingCount:    outstandingActions.total,
      lastContactAt:       recentActivity[0]?.at || null,
    };
  }, [isBroker, linkedDeals, outstandingActions, recentActivity]);

  if (!contact) {
    return (
      <div className="min-h-screen" style={{ background: "var(--cf-app-bg)" }}>
        <Sidebar />
        <main className="md:ml-[var(--cf-sidebar-w,300px)] transition-[margin-left] duration-300 ease-out flex flex-col min-h-screen items-center justify-center text-center p-10" data-testid="contact-not-found">
          <p className="cf-eyebrow">Not found</p>
          <h1 className="cf-h-page mt-2">Contact not found</h1>
          <Link to="/contacts" className="btn btn-primary mt-6"><ArrowLeft size={13} /> Back to Contacts</Link>
        </main>
      </div>
    );
  }

  const onDelete = async () => {
    if (!window.confirm(`Delete ${contact.name}? Linked deals will keep references but the contact will be removed.`)) return;
    try {
      await deleteContact(contact.id);
      toast.success(`${contact.name} deleted`);
      nav("/contacts");
    } catch (e) {
      toast.error("Could not delete contact", { description: e?.response?.data?.detail || e?.message || "Network error" });
    }
  };

  return (
    <div className="min-h-screen" style={{ background: "var(--cf-app-bg)" }}>
      <Sidebar />
      <main className="md:ml-[var(--cf-sidebar-w,300px)] transition-[margin-left] duration-300 ease-out flex flex-col min-h-screen" data-testid="contact-profile-main">
        <div className="px-4 md:px-8 lg:px-10 py-5 md:py-8 space-y-4 max-w-[1360px]">
          <div className="pl-12 md:pl-0">
            {fromDealPath ? (
              <button
                type="button"
                onClick={() => (window.history.state?.idx > 0 ? nav(-1) : nav(fromDealPath))}
                className="inline-flex items-center gap-1.5 min-h-[36px] text-[12px] font-semibold text-violet-700 hover:text-violet-900 touch-manipulation"
                data-testid="contact-back-to-deal"
              >
                <ArrowLeft size={13} /> {fromDealPath.startsWith("/deal-rooms/") ? "Back to deal" : "Back"}
              </button>
            ) : (
              <Link to="/contacts" className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-slate-600 hover:text-slate-900" data-testid="contact-back">
                <ArrowLeft size={13} /> Contacts
              </Link>
            )}
          </div>

          {/* Contact details — header card (unchanged shell). */}
          <div className="rounded-3xl bg-white border border-slate-100 p-4 md:p-5" data-testid="contact-profile-header">
            <div className="flex items-start gap-4 flex-wrap">
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-indigo-500 to-indigo-700 text-white font-display font-bold text-[20px] flex items-center justify-center cf-shadow-md shrink-0">
                {initialsOf(contact.name)}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-semibold uppercase tracking-[0.14em] ${ROLE_TONES[contact.role] || ROLE_TONES.Other}`}>{contact.role || "—"}</span>
                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-semibold uppercase tracking-[0.14em] ${STATUS_TONES[contact.status] || STATUS_TONES.Active}`} data-testid="contact-profile-status">{contact.status || "Active"}</span>
                </div>
                <h1 className="cf-h-page mt-2 !text-[24px] tracking-[-0.02em]" data-testid="contact-profile-name">{contact.name}</h1>
                <p className="text-[12.5px] text-slate-500 mt-0.5">{contact.company || "Private"}</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-1.5 gap-x-6 mt-2 max-w-[560px]">
                  {mailtoHref(contact.email) ? (
                    <a href={mailtoHref(contact.email)} className="inline-flex items-center gap-2 text-[12.5px] text-slate-700 hover:text-indigo-700" data-testid="contact-profile-mailto"><Mail size={13} /> {contact.email}</a>
                  ) : (
                    <span className="inline-flex items-center gap-2 text-[12.5px] text-slate-400"><Mail size={13} /> —</span>
                  )}
                  {telHref(contact.phone) ? (
                    <a href={telHref(contact.phone)} className="inline-flex items-center gap-2 text-[12.5px] text-slate-700 hover:text-indigo-700" data-testid="contact-profile-tel"><Phone size={13} /> {contact.phone}</a>
                  ) : (
                    <span className="inline-flex items-center gap-2 text-[12.5px] text-slate-400"><Phone size={13} /> —</span>
                  )}
                  {whatsappHref(contact.phone) ? (
                    <a href={whatsappHref(contact.phone, `Hi ${contact.name.split(" ")[0]} — quick update from ChainFlow.`)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 text-[12.5px] text-slate-700 hover:text-emerald-700" data-testid="contact-profile-whatsapp"><MessageCircle size={13} /> WhatsApp</a>
                  ) : (
                    <span className="inline-flex items-center gap-2 text-[12.5px] text-slate-400"><MessageCircle size={13} /> WhatsApp —</span>
                  )}
                  <p className="inline-flex items-center gap-2 text-[12.5px] text-slate-700"><MessageCircle size={13} /> Prefers {contact.pref || "Email"}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <ContactQuickActions
                  phone={contact.phone}
                  email={contact.email}
                  whatsappText={`Hi ${(contact.name || "").split(" ")[0]} — quick update from ChainFlow.`}
                  density="mobile"
                  testidPrefix="contact-profile"
                  className="md:hidden"
                />
                {canManage && <button onClick={() => openEditContact(contact)} className="btn btn-secondary" data-testid="contact-edit-btn"><Pencil size={13} /> Edit</button>}
                {canManage && <button onClick={onDelete} className="btn btn-ghost text-rose-600 hover:text-rose-700" data-testid="contact-delete-btn"><Trash2 size={13} /> Delete</button>}
              </div>
            </div>

            {/* Tags — inline under the header instead of a separate row. */}
            {contact.tags?.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-1.5">
                {contact.tags.map((t) => (
                  <span key={t} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-indigo-50 text-indigo-700 text-[11px] font-semibold border border-indigo-100">
                    <Tag size={9} /> {t}
                  </span>
                ))}
              </div>
            )}

            {/* iter-alignment · P48 — Quick Actions.
             * Compact affordance row: Email · Call · Add Note ·
             * View All Matters.  Each button reuses an existing
             * SSOT — the mailto/tel helpers, the same
             * `openEditContact` modal the header's Edit button
             * uses (Add Note focuses the Notes field), and an
             * in-page anchor to the Current Matters section that
             * already lives on the page.  Zero new endpoints, zero
             * duplicate state, zero hard-coded routing. */}
            <div
              className="mt-3 pt-3 border-t border-slate-100 flex flex-wrap items-center gap-2"
              data-testid="contact-quick-actions"
            >
              <a
                href={mailtoHref(contact.email) || "#"}
                onClick={(e) => { if (!mailtoHref(contact.email)) e.preventDefault(); }}
                aria-disabled={!mailtoHref(contact.email)}
                className={`inline-flex items-center gap-1.5 h-8 px-2.5 rounded-lg border border-slate-200 bg-white text-[12px] font-semibold text-slate-700 hover:text-indigo-700 hover:border-indigo-200 transition-colors ${!mailtoHref(contact.email) ? "opacity-40 cursor-not-allowed" : ""}`}
                data-testid="contact-quick-email"
              >
                <Mail size={13} /> Email
              </a>
              <a
                href={telHref(contact.phone) || "#"}
                onClick={(e) => { if (!telHref(contact.phone)) e.preventDefault(); }}
                aria-disabled={!telHref(contact.phone)}
                className={`inline-flex items-center gap-1.5 h-8 px-2.5 rounded-lg border border-slate-200 bg-white text-[12px] font-semibold text-slate-700 hover:text-indigo-700 hover:border-indigo-200 transition-colors ${!telHref(contact.phone) ? "opacity-40 cursor-not-allowed" : ""}`}
                data-testid="contact-quick-call"
              >
                <Phone size={13} /> Call
              </a>
              {canManage && <button
                type="button"
                onClick={() => openEditContact(contact)}
                className="inline-flex items-center gap-1.5 h-8 px-2.5 rounded-lg border border-slate-200 bg-white text-[12px] font-semibold text-slate-700 hover:text-indigo-700 hover:border-indigo-200 transition-colors"
                data-testid="contact-quick-add-note"
                aria-label="Add or edit relationship note"
              >
                <StickyNote size={13} /> Add Note
              </button>}
              {/* P3 · Contact Profile · Transactions Management —
                  the previous "View All Transactions" anchor
                  (grey scroll-to-section shim, disabled at 0
                  matters) is replaced with an ACTIVE
                  "Transactions" button that opens the lightweight
                  transactions manager modal.  The button is
                  always enabled — an operator with zero linked
                  deals can now use it to ADD the contact to their
                  first Deal Room without leaving the profile.
                  The counter badge is preserved so the row still
                  reads at scan speed. */}
              <button
                type="button"
                onClick={() => setTransactionsOpen(true)}
                className="inline-flex items-center gap-1.5 h-8 px-2.5 rounded-lg border border-slate-200 bg-white text-[12px] font-semibold text-slate-700 hover:text-indigo-700 hover:border-indigo-200 transition-colors"
                data-testid="contact-quick-view-matters"
                aria-label="Manage transactions"
              >
                <FileText size={13} /> Transactions
                {linkedDeals.length > 0 && (
                  <span className="ml-0.5 inline-flex items-center justify-center min-w-[16px] h-4 px-1 rounded bg-indigo-50 text-indigo-700 text-[10px] font-bold tabular-nums">
                    {linkedDeals.length}
                  </span>
                )}
              </button>
            </div>
          </div>

          {/* ── Solicitor KPI strip + primary CTAs ───────────────
              iter-361 · F-10 · Solicitor Contact Profile
              Refinement (2026-08-29) — only visible when the
              contact's role is Solicitor.  Every tile is derived
              from canonical SSOT already resolved above.  Metrics
              we cannot compute reliably surface as a calm "—"
              placeholder — never invented values. */}
          {isSolicitor && solicitorKpis && (
            <div
              className="rounded-2xl bg-white border border-slate-100 p-3 md:p-4"
              data-testid="contact-solicitor-kpis"
            >
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2.5 flex-1 min-w-0">
                  <KpiTile
                    icon={<Briefcase size={12} />}
                    label="Active Matters"
                    value={solicitorKpis.activeCount}
                    testid="solicitor-kpi-active"
                  />
                  <KpiTile
                    icon={<ShieldAlert size={12} />}
                    label="At Risk"
                    value={solicitorKpis.atRiskCount}
                    tone={solicitorKpis.atRiskCount > 0 ? "danger" : "calm"}
                    testid="solicitor-kpi-at-risk"
                  />
                  <KpiTile
                    icon={<Timer size={12} />}
                    label="Avg Response"
                    value={solicitorKpis.avgResponse ?? "—"}
                    tone="calm"
                    unavailable={solicitorKpis.avgResponse === null}
                    tooltip="Awaiting message-direction data on Deal Room communications"
                    testid="solicitor-kpi-response"
                  />
                  <KpiTile
                    icon={<EnquiryIcon size={12} />}
                    label="Outstanding Enquiries"
                    value={solicitorKpis.enquiriesCount}
                    tone={solicitorKpis.enquiriesCount > 0 ? "warn" : "calm"}
                    testid="solicitor-kpi-enquiries"
                  />
                  <KpiTile
                    icon={<Clock size={12} />}
                    label="Last Contact"
                    value={_fmtRelative(solicitorKpis.lastContactAt) || "—"}
                    tone="calm"
                    unavailable={!solicitorKpis.lastContactAt}
                    testid="solicitor-kpi-last-contact"
                  />
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button
                    type="button"
                    onClick={() => setTransactionsOpen(true)}
                    className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg text-[12.5px] font-semibold text-white shadow-sm hover:shadow-[0_4px_12px_-2px_rgba(99,102,241,0.45)] transition-shadow"
                    style={{ background: "linear-gradient(135deg, #6366F1 0%, #4F46E5 100%)" }}
                    data-testid="solicitor-cta-instruct"
                    aria-label="Instruct or refer this solicitor to a transaction"
                  >
                    <UserPlus size={13} strokeWidth={2.4} /> Instruct / Refer Solicitor
                  </button>
                  <button
                    type="button"
                    onClick={() => setTransactionsOpen(true)}
                    className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border border-slate-200 bg-white text-[12.5px] font-semibold text-slate-700 hover:text-indigo-700 hover:border-indigo-200 transition-colors"
                    data-testid="solicitor-cta-add-transaction"
                    aria-label="Attach solicitor to an existing transaction"
                  >
                    <FileText size={13} /> Add to Transaction
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* ── Mortgage Broker KPI strip ───────────────────────
              iter-alignment · Broker Contact Profile Refinement
              (2026-02-28) — only visible when the contact's role
              is Mortgage Broker.  Every tile is derived from
              canonical SSOT already resolved above (linkedDeals,
              outstandingActions, recentActivity, mortgageStatus
              + completedSteps journey evidence).  Never invents
              a figure — an un-derivable metric surfaces as "—". */}
          {isBroker && brokerKpis && (
            <div
              className="rounded-2xl bg-white border border-slate-100 p-3 md:p-4"
              data-testid="contact-broker-kpis"
            >
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2.5 flex-1 min-w-0">
                  <KpiTile
                    icon={<Briefcase size={12} />}
                    label="Active Cases"
                    value={brokerKpis.activeCount}
                    testid="broker-kpi-active"
                  />
                  <KpiTile
                    icon={<ShieldAlert size={12} />}
                    label="At Risk"
                    value={brokerKpis.atRiskCount}
                    tone={brokerKpis.atRiskCount > 0 ? "danger" : "calm"}
                    testid="broker-kpi-at-risk"
                  />
                  <KpiTile
                    icon={<BadgeCheck size={12} />}
                    label="Mortgage Offers"
                    value={brokerKpis.mortgageOffersCount}
                    tone={brokerKpis.mortgageOffersCount > 0 ? "calm" : "calm"}
                    testid="broker-kpi-offers"
                  />
                  <KpiTile
                    icon={<AlertTriangle size={12} />}
                    label="Outstanding Actions"
                    value={brokerKpis.outstandingCount}
                    tone={brokerKpis.outstandingCount > 0 ? "warn" : "calm"}
                    testid="broker-kpi-outstanding"
                  />
                  <KpiTile
                    icon={<Clock size={12} />}
                    label="Last Contact"
                    value={_fmtRelative(brokerKpis.lastContactAt) || "—"}
                    tone="calm"
                    unavailable={!brokerKpis.lastContactAt}
                    testid="broker-kpi-last-contact"
                  />
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button
                    type="button"
                    onClick={() => setTransactionsOpen(true)}
                    className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg text-[12.5px] font-semibold text-white shadow-sm hover:shadow-[0_4px_12px_-2px_rgba(16,185,129,0.45)] transition-shadow"
                    style={{ background: "linear-gradient(135deg, #10B981 0%, #059669 100%)" }}
                    data-testid="broker-cta-instruct"
                    aria-label="Instruct or refer this mortgage broker to a transaction"
                  >
                    <UserPlus size={13} strokeWidth={2.4} /> Instruct / Refer Broker
                  </button>
                  <button
                    type="button"
                    onClick={() => setTransactionsOpen(true)}
                    className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border border-slate-200 bg-white text-[12.5px] font-semibold text-slate-700 hover:text-indigo-700 hover:border-indigo-200 transition-colors"
                    data-testid="broker-cta-add-transaction"
                    aria-label="Attach broker to an existing transaction"
                  >
                    <FileText size={13} /> Add to Transaction
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* ── Live Transactions ────────────────────────────────
              Every row → Deal Room.  Shows address, role,
              current stage, exchange target and health chip.
              iter-P68 · Renamed "Current Matters" → "Live
              Transactions"; the section shell + testids are
              preserved so downstream tests still resolve. */}
          <Section
            eyebrow="Live Transactions"
            heading={`${linkedDeals.length} ${linkedDeals.length === 1 ? "transaction" : "transactions"}`}
            testid="contact-current-matters"
            hidden={linkedDeals.length === 0}
            chip={linkedDeals.length > 4 ? (
              <a
                href="#contact-current-matters"
                className="text-[12px] font-semibold text-indigo-700 hover:text-indigo-900"
                data-testid="contact-current-matters-view-all"
                onClick={(e) => {
                  e.preventDefault();
                  const el = document.getElementById("contact-current-matters-anchor");
                  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
                }}
              >
                View all
              </a>
            ) : null}
          >
            <div id="contact-current-matters-anchor" data-testid="contact-linked-deals" />
            <div data-testid="contact-transactions" style={{ display: "none" }} />
            {/* iter-P68 · Inline "Transactions Awaiting {FirstName}"
                banner — surfaces the same blocker count the
                Outstanding Actions section derives, but INLINE at
                the top of Live Transactions so the operator sees
                what needs chasing without a scroll.  Reuses the
                existing `outstandingActions.blockedMattersCount`
                SSOT — no new state, no new endpoint. */}
            {outstandingActions.blockedMattersCount > 0 && (
              <div
                className="mb-3 flex items-center justify-between gap-3 rounded-lg border border-amber-200/60 bg-amber-50/70 px-3 py-2"
                data-testid="contact-transactions-awaiting-banner"
                role="status"
              >
                <div className="flex items-center gap-2 min-w-0">
                  <span
                    className="w-6 h-6 rounded-full bg-amber-500 text-white inline-flex items-center justify-center shrink-0"
                    aria-hidden
                  >
                    <AlertTriangle size={12} strokeWidth={2.4} />
                  </span>
                  <p className="text-[12.5px] font-semibold text-amber-900 truncate">
                    {outstandingActions.blockedMattersCount} {outstandingActions.blockedMattersCount === 1 ? "transaction awaiting" : "transactions awaiting"} {contact.name.split(" ")[0]}
                  </p>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  <a
                    href="#contact-outstanding-actions"
                    className="inline-flex items-center h-7 px-2.5 rounded-md text-[11.5px] font-semibold text-slate-700 bg-white border border-slate-200 hover:bg-slate-50 transition-colors"
                    data-testid="contact-transactions-awaiting-review"
                    onClick={(e) => {
                      e.preventDefault();
                      const el = document.querySelector('[data-testid="contact-outstanding-actions"]');
                      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
                    }}
                  >
                    Review
                  </a>
                  <Link
                    to={buildRequestDocUrl(outstandingActions.them[0]?.dealId || outstandingActions.us[0]?.dealId) || `/deal-rooms/${outstandingActions.them[0]?.dealId || outstandingActions.us[0]?.dealId || ""}`}
                    className="inline-flex items-center gap-1 h-7 px-2.5 rounded-md text-[11.5px] font-semibold text-white transition-shadow hover:shadow-[0_4px_10px_-2px_rgba(217,119,6,0.45)]"
                    style={{ background: "linear-gradient(135deg, #F97316 0%, #EA580C 100%)" }}
                    data-testid="contact-transactions-awaiting-chase"
                  >
                    Chase Now <Send size={11} strokeWidth={2.4} />
                  </Link>
                </div>
              </div>
            )}
            <ul className="divide-y divide-slate-100/70" data-testid="contact-transactions-list">
                {linkedDeals.slice(0, 4).map((d) => {
                  const prop = findProperty(d.propertyId);
                  const role = _roleForDeal(d, contact.id);
                  const status = dealStatusFor(d);
                  const healthTone = _HEALTH_TONES[status.band] || _HEALTH_TONES.healthy;
                  const healthLabel = status.label?.canonical || "On Track";
                  const currentStageId = nodeIdForStage(String(d.stage || "").toLowerCase());
                  const currentStageLabel =
                    nodeById(currentStageId)?.label || stageLabel(d.stage);
                  const exchangeIso = getDealExchangeTarget(d);
                  const exchangeLabel = _fmtTargetDate(exchangeIso);
                  // iter-P68 · Overdue detection — target exchange is past
                  // and the deal hasn't yet reached exchange.  Stronger
                  // typography on the tail-chip + red tone.
                  const exchangeOverdue = (() => {
                    if (!exchangeIso) return false;
                    const t = Date.parse(exchangeIso);
                    if (Number.isNaN(t)) return false;
                    if (t > Date.now()) return false;
                    const s = String(d.stage || "").toLowerCase();
                    return !(s === "exchanged" || s === "completed" || s === "exchange" || s === "complete");
                  })();
                  // Responsibility / blocker hint — first live blocker
                  // for this deal, if any.  Reuses the exact same
                  // `deal.blockers` SSOT the Deal Room reads.
                  const liveBlocker = Array.isArray(d.blockers)
                    ? d.blockers.find((b) => b && b.status !== "resolved")
                    : null;
                  const responsibility = liveBlocker
                    ? _blockerTitle(liveBlocker)
                    : null;
                  return (
                    <li
                      key={d.id}
                      className="py-2.5 flex items-start gap-3"
                      data-testid={`contact-transaction-${d.id}`}
                      data-matter-id={d.id}
                      data-role={role}
                      data-health-band={status.band}
                    >
                      <div className="w-9 h-9 rounded-xl bg-slate-100 overflow-hidden border border-slate-100/70 shrink-0 mt-0.5" data-testid={`contact-transaction-thumb-${d.id}`}>
                        {prop?.thumb ? (
                          <img src={prop.thumb} alt="" className="w-full h-full object-cover" />
                        ) : (
                          // iter-alignment · P3 · Live Transaction
                          // Property Data (2026-02-28) — when the
                          // Property SSOT lacks a thumb, render the
                          // first letter of the canonical address
                          // as a lightweight placeholder rather than
                          // an empty grey square.  Purely visual —
                          // survives refresh because the address is
                          // read from the SSOT on every render.
                          <div
                            className="w-full h-full inline-flex items-center justify-center text-[13px] font-bold text-slate-500 bg-gradient-to-br from-slate-50 to-slate-100"
                            aria-hidden
                          >
                            {(prop?.address || d.name || "?").trim().charAt(0).toUpperCase()}
                          </div>
                        )}
                      </div>
                      {/* iter-P62 · Deep-link the primary content
                          column so tapping the property / blocker /
                          stage anywhere opens the Deal Room.  The
                          right-tail Open button is preserved for
                          keyboard + explicit affordance parity.
                          iter-P68 · full-row hover elevation + trailing
                          chevron slide animation. */}
                      <Link
                        to={`/deal-rooms/${d.id}`}
                        className="min-w-0 flex-1 group/matter cursor-pointer"
                        data-testid={`contact-matter-deeplink-${d.id}`}
                        aria-label={`Open Deal Room for ${prop?.address || d.name || "this transaction"}`}
                      >
                        {/* Row 1 · Property (dominant).
                            iter-alignment · P3 · Live Transaction Property
                            Data (2026-02-28) — the fallback chain now
                            prefers the Deal Room's own `name` (which
                            typically mirrors the property address) BEFORE
                            the generic "Property" label so operators
                            never see the useless generic string when the
                            SSOT actually knows what to render. */}
                        <p
                          className="text-[13px] font-bold text-slate-900 truncate tracking-[-0.01em] group-hover/matter:text-indigo-700 transition-colors"
                          data-testid={`contact-transaction-address-${d.id}`}
                        >
                          {prop?.address?.split(",")[0] || d.name || "Property"}
                        </p>
                        {/* Row 2 · Primary blocker — strongest secondary
                            text so the eye moves from Property → what's
                            standing in the way. */}
                        {responsibility && (
                          <p
                            className="mt-1 text-[11.5px] font-semibold text-amber-700 truncate inline-flex items-center gap-1.5 bg-amber-50/60 rounded-md px-1.5 py-0.5"
                            data-testid={`contact-matter-blocker-${d.id}`}
                          >
                            <AlertTriangle size={11} strokeWidth={2.2} /> {responsibility}
                          </p>
                        )}
                        {/* Row 3 · Stage · Participant (role) — lighter,
                            calmer, less saturated per P49.1 polish.
                            iter-alignment · Broker Contact Profile Refinement
                            (2026-02-28) — when the profile role is
                            Mortgage Broker, the row is additionally
                            enriched with Buyer · Mortgage Stage ·
                            Lender directly beneath the stage/role
                            line.  Every field is read from canonical
                            SSOT already resolved above; missing
                            values are hidden cleanly (never
                            fabricated). */}
                        <div className="mt-0.5 flex flex-wrap items-center gap-1.5 text-[10.5px] text-slate-500">
                          <span
                            className="inline-flex items-center px-1.5 h-[16px] rounded font-medium bg-slate-50 text-slate-500 border border-slate-100"
                            data-testid={`contact-transaction-stage-${d.id}`}
                          >
                            {currentStageLabel}
                          </span>
                          <span aria-hidden className="text-slate-300">·</span>
                          <span data-testid={`contact-transaction-role-${d.id}`}>
                            {role}
                          </span>
                          <span
                            className={`ml-1.5 inline-flex items-center px-1.5 h-[16px] rounded font-medium border ${healthTone}`}
                            data-testid={`contact-transaction-health-${d.id}`}
                          >
                            {healthLabel}
                          </span>
                        </div>
                        {isBroker && (() => {
                          // Buyer resolution — prefer the canonical FK
                          // (`deal.buyerId`) then fall back to the
                          // aggregated `deal.participants` list (typed
                          // rows from Postgres `deal_participants`)
                          // so buyer names still surface for deals
                          // whose buyer sits in the participant table
                          // rather than the FK slot.  No fabrication —
                          // resolves to `null` if no signal exists.
                          let buyerName = null;
                          if (d.buyerId) {
                            const buyer = findContact(d.buyerId);
                            buyerName = buyer?.name || null;
                          }
                          if (!buyerName && Array.isArray(d.participants)) {
                            const buyerRow = d.participants.find(
                              (p) => String(p?.participant_type || "").toLowerCase() === "buyer",
                            );
                            buyerName = buyerRow?.full_name || null;
                          }
                          const mortgageStage = _resolveMortgageStage(d);
                          const mortgageTone = mortgageStage ? _MORTGAGE_STAGE_TONES[mortgageStage] : null;
                          const lender = d.lender || null;
                          // Waiting-on party summary — reuse the same
                          // canonical Waiting-On envelope the Deal
                          // Rooms list rail reads (`d.waiting_on`).
                          const waitingOwner =
                            (d.waiting_on && (d.waiting_on.owner_label || d.waiting_on.owner_role))
                            || (liveBlocker && (liveBlocker.owner_label || liveBlocker.owner_role))
                            || null;
                          if (!buyerName && !mortgageStage && !lender && !waitingOwner) return null;
                          return (
                            <div
                              className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] text-slate-500"
                              data-testid={`contact-transaction-broker-meta-${d.id}`}
                            >
                              {buyerName && (
                                <span className="inline-flex items-center gap-1" data-testid={`contact-transaction-buyer-${d.id}`}>
                                  <UserPlus size={10} strokeWidth={2.2} className="text-slate-400" />
                                  <span className="font-medium text-slate-700">{buyerName}</span>
                                </span>
                              )}
                              {mortgageStage && (
                                <>
                                  {buyerName && <span aria-hidden className="text-slate-300">·</span>}
                                  <span
                                    className={`inline-flex items-center px-1.5 h-[16px] rounded font-semibold border ${mortgageTone}`}
                                    data-testid={`contact-transaction-mortgage-stage-${d.id}`}
                                    title={`Mortgage stage: ${mortgageStage}`}
                                  >
                                    {mortgageStage}
                                  </span>
                                </>
                              )}
                              {lender && (
                                <>
                                  <span aria-hidden className="text-slate-300">·</span>
                                  <span
                                    className="inline-flex items-center gap-1"
                                    data-testid={`contact-transaction-lender-${d.id}`}
                                  >
                                    <Landmark size={10} strokeWidth={2.2} className="text-slate-400" />
                                    <span className="font-medium text-slate-700">{lender}</span>
                                  </span>
                                </>
                              )}
                              {waitingOwner && (
                                <>
                                  <span aria-hidden className="text-slate-300">·</span>
                                  <span
                                    className="inline-flex items-center gap-1 text-amber-700"
                                    data-testid={`contact-transaction-waiting-${d.id}`}
                                    title="Waiting on"
                                  >
                                    <Clock size={10} strokeWidth={2.2} />
                                    Waiting on {waitingOwner}
                                  </span>
                                </>
                              )}
                            </div>
                          );
                        })()}
                      </Link>
                      {/* Row 4 · Exchange date · right-aligned tail. */}
                      <div className="flex flex-col items-end gap-1 shrink-0">
                        {exchangeLabel && (
                          <span
                            className={`text-[12px] font-bold tabular-nums inline-flex items-center gap-1 ${exchangeOverdue ? "text-rose-700" : "text-slate-600"}`}
                            data-testid={`contact-matter-exchange-${d.id}`}
                            data-exchange-overdue={exchangeOverdue ? "true" : "false"}
                            title={exchangeOverdue ? "Exchange target overdue" : "Target exchange date"}
                          >
                            <Calendar size={11} strokeWidth={2.2} /> {exchangeLabel}
                            {exchangeOverdue && (
                              <span className="ml-1 px-1 h-[15px] inline-flex items-center rounded-sm bg-rose-100 text-rose-700 text-[9.5px] font-bold uppercase tracking-[0.06em]">
                                Overdue
                              </span>
                            )}
                          </span>
                        )}
                        {/* iter-P65 · Contextual Request Document —
                            only surfaces when the matter has an open
                            doc-related blocker so the operator lands
                            straight in the canonical Request drawer
                            for that Deal Room. */}
                        {isDocRequestRelevant(responsibility) && (
                          <Link
                            to={buildRequestDocUrl(d.id) || `/deal-rooms/${d.id}`}
                            className="inline-flex items-center gap-1 h-7 px-2 rounded-md text-[11px] font-semibold text-violet-700 hover:text-violet-900 hover:bg-violet-50 transition-colors"
                            data-testid={`contact-matter-request-doc-${d.id}`}
                            title="Request a document for this transaction"
                          >
                            <FileText size={10} strokeWidth={2} /> Request
                          </Link>
                        )}
                        <Link
                          to={`/deal-rooms/${d.id}`}
                          className="inline-flex items-center gap-1 h-7 px-2 rounded-md text-[11px] font-semibold text-slate-700 hover:text-indigo-700 hover:bg-slate-50 transition-colors group/open"
                          data-testid={`contact-deal-${d.id}`}
                          aria-label={`Open Deal Room for ${prop?.address || "this transaction"}`}
                        >
                          Open
                          <ChevronRight
                            size={11}
                            strokeWidth={2.4}
                            className="transition-transform group-hover/open:translate-x-0.5"
                          />
                        </Link>
                      </div>
                    </li>
                  );
                })}
              </ul>
              {linkedDeals.length > 4 && (
                <p
                  className="mt-2 text-[12px] font-semibold text-indigo-700 hover:text-indigo-900 cursor-pointer"
                  data-testid="contact-current-matters-more"
                  onClick={() => {
                    const el = document.getElementById("contact-current-matters-anchor");
                    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
                  }}
                >
                  + {linkedDeals.length - 4} more {linkedDeals.length - 4 === 1 ? "transaction" : "transactions"}
                </p>
              )}
          </Section>

          {/* ── Outstanding Actions ─────────────────────────────
              Hidden when no live blockers exist across any of
              the contact's matters.  Reuses `deal.blockers`
              SSOT + `owner_role` for the Waiting-on-them /
              Waiting-on-us split. */}
          <Section
            eyebrow="Outstanding Actions"
            heading={`${outstandingActions.total} open`}
            testid="contact-outstanding-actions"
            hidden={outstandingActions.total === 0}
            chip={outstandingActions.blockedMattersCount > 0 ? (
              <span
                className="inline-flex items-center gap-1 text-[11px] font-semibold text-amber-700 bg-amber-50/60 rounded-md px-2 py-0.5"
                data-testid="contact-outstanding-blocker-summary"
                title="Number of matters currently blocked by an open action"
              >
                <AlertTriangle size={11} strokeWidth={2.2} />
                Blocking {outstandingActions.blockedMattersCount} {outstandingActions.blockedMattersCount === 1 ? "matter" : "matters"}
              </span>
            ) : null}
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {outstandingActions.them.length > 0 && (
                <div data-testid="contact-actions-waiting-on-them">
                  <p className="text-[10.5px] uppercase tracking-[0.10em] font-semibold text-amber-700 mb-1.5">
                    Outstanding with {contact.name.split(" ")[0]} · {outstandingActions.them.length}
                  </p>
                  <ul className="space-y-1.5">
                    {outstandingActions.them.map((a, i) => (
                      <li
                        key={a.id}
                        className="text-[12px] text-slate-700 leading-snug border border-slate-100 rounded-lg px-2.5 py-1.5 hover:border-slate-200 hover:bg-slate-50/40 transition-colors"
                        data-testid={`contact-action-them-${a.dealId}-${i}`}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0 flex-1">
                            <span className="font-semibold">{a.title}</span>
                            {a.detail && <span className="text-slate-500"> · {a.detail}</span>}
                            <Link to={`/deal-rooms/${a.dealId}`} className="block mt-0.5 text-[10.5px] text-indigo-700 hover:text-indigo-900 truncate">
                              {a.address}
                            </Link>
                          </div>
                          {isDocRequestRelevant(`${a.title || ""} ${a.detail || ""}`) && (
                            <Link
                              to={buildRequestDocUrl(a.dealId) || `/deal-rooms/${a.dealId}`}
                              className="shrink-0 inline-flex items-center gap-1 h-6 px-2 rounded-md text-[10.5px] font-semibold text-violet-700 hover:text-violet-900 hover:bg-violet-50 transition-colors"
                              data-testid={`contact-action-them-request-${a.dealId}-${i}`}
                              title="Request the document via the canonical drawer"
                            >
                              <FileText size={9} strokeWidth={2.2} /> Request
                            </Link>
                          )}
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {outstandingActions.us.length > 0 && (
                <div data-testid="contact-actions-waiting-on-us">
                  <p className="text-[10.5px] uppercase tracking-[0.10em] font-semibold text-rose-700 mb-1.5">
                    Outstanding with us · {outstandingActions.us.length}
                  </p>
                  <ul className="space-y-1.5">
                    {outstandingActions.us.map((a, i) => (
                      <li
                        key={a.id}
                        className="text-[12px] text-slate-700 leading-snug border border-slate-100 rounded-lg px-2.5 py-1.5 hover:border-slate-200 hover:bg-slate-50/40 transition-colors"
                        data-testid={`contact-action-us-${a.dealId}-${i}`}
                      >
                        <span className="font-semibold">{a.title}</span>
                        {a.detail && <span className="text-slate-500"> · {a.detail}</span>}
                        <Link to={`/deal-rooms/${a.dealId}`} className="block mt-0.5 text-[10.5px] text-indigo-700 hover:text-indigo-900 truncate">
                          {a.address}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </Section>

          {/* ── Open Enquiries ──────────────────────────────────
              Reads `deal.enquiries[]` when the store has
              hydrated it client-side (populated by the Deal Room
              on visit).  Hidden when empty. */}
          <Section
            eyebrow="Open Enquiries"
            heading={`${openEnquiries.length} open`}
            testid="contact-open-enquiries"
            hidden={openEnquiries.length === 0}
          >
            <ul className="space-y-1.5">
              {openEnquiries.slice(0, 6).map((e) => (
                <li
                  key={e.id}
                  className="text-[12px] text-slate-700 leading-snug border border-slate-100 rounded-lg px-2.5 py-1.5 flex items-center gap-2 hover:border-slate-200 hover:bg-slate-50/40 transition-colors"
                  data-testid={`contact-enquiry-${e.dealId}-${e.id}`}
                >
                  <HelpCircle size={12} className="text-amber-600 shrink-0" />
                  <span className="flex-1 min-w-0 truncate">{e.body}</span>
                  <Link to={`/deal-rooms/${e.dealId}`} className="text-[10.5px] text-indigo-700 hover:text-indigo-900 truncate max-w-[140px]">
                    {e.address}
                  </Link>
                </li>
              ))}
            </ul>
          </Section>

          {/* ── Recent Activity ─────────────────────────────────
              Aggregated `deal.activity` across matters, top 8. */}
          <Section
            eyebrow="Recent Activity"
            heading="Across matters"
            testid="contact-recent-activity"
            hidden={recentActivity.length === 0}
          >
            <ul className="space-y-1.5">
              {recentActivity.map((r) => (
                <li
                  key={r.id}
                  className="text-[12px] text-slate-700 leading-snug border border-slate-100 rounded-lg px-2.5 py-1.5 flex items-center gap-2 hover:border-slate-200 hover:bg-slate-50/40 transition-colors"
                  data-testid={`contact-activity-${r.dealId}-${r.id}`}
                >
                  <ActivityIcon size={12} className="text-slate-500 shrink-0" />
                  <span className="flex-1 min-w-0 truncate">{r.text}</span>
                  <span className="text-[10.5px] text-slate-400 tabular-nums whitespace-nowrap">{_fmtRelative(r.at)}</span>
                  <Link to={`/deal-rooms/${r.dealId}`} className="text-[10.5px] text-indigo-700 hover:text-indigo-900 truncate max-w-[140px]">
                    {r.address}
                  </Link>
                </li>
              ))}
            </ul>
          </Section>

          {/* ── Recent Communication ────────────────────────────
              Aggregated `deal.messages`, top 5, non-internal. */}
          <Section
            eyebrow="Recent Communication"
            heading={`${recentComms.length} recent`}
            testid="contact-recent-communication"
            hidden={recentComms.length === 0}
          >
            <ul className="space-y-1.5">
              {recentComms.map((m) => (
                <li
                  key={m.id}
                  className="text-[12px] text-slate-700 leading-snug border border-slate-100 rounded-lg px-2.5 py-1.5 flex items-start gap-2 hover:border-slate-200 hover:bg-slate-50/40 transition-colors"
                  data-testid={`contact-comms-${m.dealId}-${m.id}`}
                >
                  <MessageSquare size={12} className="text-indigo-500 shrink-0 mt-0.5" />
                  <span className="flex-1 min-w-0 line-clamp-2">{m.body}</span>
                  {m.demo && <DemoBadge testid={`contact-comms-demo-badge-${m.id}`} />}
                  <div className="flex items-center gap-1.5 shrink-0">
                    <span className="text-[10.5px] text-slate-400 tabular-nums whitespace-nowrap">{_fmtRelative(m.at)}</span>
                    <Link to={`/deal-rooms/${m.dealId}`} className="text-[10.5px] text-indigo-700 hover:text-indigo-900 truncate max-w-[140px]">
                      {m.address}
                    </Link>
                  </div>
                </li>
              ))}
            </ul>
          </Section>

          {/* ── Open Document Requests ──────────────────────────
              Reuses `getPilotDocumentRequests(dealId)` SSOT.
              Hidden when no pending requests exist.
              iter-alignment · P49.1 — Row tint reflects urgency
              from the existing `due_date` field: neutral grey /
              amber / red.  Zero new state, zero new SSOT. */}
          <Section
            eyebrow="Open Document Requests"
            heading={`${openDocRequests.length} pending`}
            testid="contact-open-doc-requests"
            hidden={openDocRequests.length === 0}
          >
            <ul className="space-y-1.5">
              {openDocRequests.slice(0, 6).map((r) => {
                const bandChip = r.dueBand === "overdue"
                  ? "bg-rose-50 text-rose-700 border-rose-100"
                  : r.dueBand === "today"
                    ? "bg-amber-50 text-amber-700 border-amber-100"
                    : "bg-slate-50 text-slate-500 border-slate-100";
                const iconTone = r.dueBand === "overdue"
                  ? "text-rose-500"
                  : r.dueBand === "today"
                    ? "text-amber-500"
                    : "text-slate-500";
                return (
                  <li
                    key={r.id}
                    className="text-[12px] text-slate-700 leading-snug border border-slate-100 rounded-lg px-2.5 py-1.5 flex items-center gap-2 hover:border-slate-200 hover:bg-slate-50/40 transition-colors"
                    data-testid={`contact-doc-request-${r.dealId}-${r.id}`}
                    data-due-band={r.dueBand}
                  >
                    <FileText size={12} className={`${iconTone} shrink-0`} />
                    <span className="flex-1 min-w-0 truncate">
                      <span className="font-semibold">{r.title}</span>
                      {r.from && <span className="text-slate-500"> · from {r.from}</span>}
                    </span>
                    {r.dueLabel && (
                      <span
                        className={`inline-flex items-center px-1.5 h-[18px] rounded-md text-[10.5px] font-semibold border ${bandChip}`}
                        data-testid={`contact-doc-request-due-${r.dealId}-${r.id}`}
                      >
                        {r.dueLabel}
                      </span>
                    )}
                    <Link to={`/deal-rooms/${r.dealId}`} className="text-[10.5px] text-indigo-700 hover:text-indigo-900 truncate max-w-[140px]">
                      {r.address}
                    </Link>
                  </li>
                );
              })}
            </ul>
          </Section>

          {/* ── Working Notes ─────────────────────────────────────
              iter-alignment · P48 · P68 — Renamed from
              "Relationship Notes" → "Working Notes" per the P68
              terminology contract.  Same `contact.notes` SSOT
              (no new field, no new endpoint).  Hidden when empty.
              Body clamps to the first ~3 bullets/lines with a
              "View all notes" affordance that expands inline. */}
          {contact.notes && (
            <Section
              eyebrow="Working Notes"
              heading="Private context on this contact"
              testid="contact-relationship-notes"
              hidden={false}
              chip={canManage ? (
                <button
                  type="button"
                  onClick={() => openEditContact(contact)}
                  className="opacity-0 focus:opacity-100 group-hover:opacity-100 hover:opacity-100 transition-opacity inline-flex items-center gap-1 h-7 px-2 rounded-md text-[11.5px] font-semibold text-slate-500 hover:text-indigo-700 hover:bg-slate-50"
                  data-testid="contact-relationship-notes-edit"
                  aria-label="Edit working notes"
                >
                  <Pencil size={11} strokeWidth={2.2} />
                </button>
              ) : null}
            >
              <div className="group">
                {(() => {
                  const lines = contact.notes.split("\n").filter(Boolean);
                  const isLong = lines.length > 3;
                  if (!isLong) {
                    return (
                      <p
                        className="text-[12.5px] leading-relaxed text-slate-700 whitespace-pre-line"
                        data-testid="contact-relationship-notes-body"
                      >{contact.notes}</p>
                    );
                  }
                  return (
                    <details className="group/notes" data-testid="contact-relationship-notes-details">
                      <p
                        className="text-[12.5px] leading-relaxed text-slate-700 whitespace-pre-line line-clamp-3 group-open/notes:line-clamp-none"
                        data-testid="contact-relationship-notes-body"
                      >{contact.notes}</p>
                      <summary
                        className="mt-1.5 list-none cursor-pointer text-[11.5px] font-semibold text-indigo-700 hover:text-indigo-900 transition-colors select-none"
                        data-testid="contact-relationship-notes-toggle"
                      >
                        <span className="group-open/notes:hidden">View all notes</span>
                        <span className="hidden group-open/notes:inline">Show fewer</span>
                      </summary>
                    </details>
                  );
                })()}
              </div>
            </Section>
          )}
        </div>
      </main>
      {/* P3 · Transactions manager modal — mounted at page root
          so it overlays the entire profile.  Rendering is gated
          by the `transactionsOpen` flag; the modal manages its
          own escape / backdrop handlers. */}
      <ContactTransactionsModal
        open={transactionsOpen}
        onClose={() => setTransactionsOpen(false)}
        contact={contact}
      />
    </div>
  );
}
